import {BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, throwError} from 'rxjs';
import {HierarchyService} from '../../shared/hierarchy.service';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {Entity} from 'smag-commons/cfg/hierarchy';
import {NetworkService} from '../../shared/network.service';
import {
    TimeFilter,
    TimeFilterComponent,
    TimeFilterProfile,
    TimeFilterType, TimeRange
} from '../../shared/time-filter/time-filter.component';
import {MatSelectChange} from '@angular/material/select';
import {SmagApiService} from '../../api/smag-api.service';
import {getStatisticalProfileDeviceKeys, hasStatisticalProfile} from 'smag-commons/fcn';
import {errorCode, isDefined} from '../../commons/commons';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {Inject} from '@angular/core';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {ResizeService} from '../../shared/resize.service';
import {hasFieldEntityDevice} from '../../shared/common';
import {
    EntityMeasureAggregationType,
    MeasureAggregationDataSource,
    Permission,
    TIME_UNITS,
    TimeAggregationType
} from 'smag-commons/dto/api';
import {ECSelection} from '../../shared/ec-resource-tree/ec-resource-tree.component';
import {ErrorCode} from '../../api/errors';
import * as moment from 'moment-timezone';
import {ConfigurationService} from '../../shared/configuration.service';
import {DEFAULT_TIMEZONE} from '../../i18n/i18n-defaults';

export interface TimeResolution {
    name: string;
    resolution: number; // minutes-per-unit
}

export type DataFactory = (rangeRefreshObs: Observable<[number, number]>, timeRes: number) => Observable<MeasureData[]>;

interface BaseMeasure {
    name: string;
    dataType: MeasureDataType;
}

export interface MeasureOption extends BaseMeasure {
    dataFactory: DataFactory;
}

// measures shown depend on resource's type
export interface Measure extends BaseMeasure {
    data: Observable<MeasureData[]>;
}

export enum MeasureDataType {
    MONITORED,
    STATISTICAL,
    HYBRID // used only for overall data type
}

export interface MeasureData {
    data: [number, number][]; // [time, value]
    subName?: string;
    color?: string;
    consolidation_color?: string;
    minValue?: number;
    maxValue?: number;
    meanValue?: number;
    sumValue?: number;
    fallbackTimes?: Set<number>;
    uom?: string;
}

export interface MeasureResource extends Entity {
    id: string;
    name: string;
    permissions: Permission[];
}

export interface DataSources {
    dataSource: MeasureAggregationDataSource;
}

export interface TimeAggregationValue {
    timeAggregation: TimeAggregationType;
    value: number;
}

export interface MeasureAggregationWithType {
    measureAggregation: EntityMeasureAggregationType;
    timeAggregation: TimeAggregationType;
    coefficient?: number;
}

export const TIME_RESOLUTIONS: TimeResolution[] = [
    {
        name: '1 minute',
        resolution: 1
    },
    {
        name: '5 minutes',
        resolution: 5
    },
    {
        name: '15 minutes',
        resolution: 15
    },
    {
        name: '1 hour',
        resolution: 60
    },
    {
        name: '1 day',
        resolution: 1440
    }
];

const DEFAULT_TIME_FILTER = {
    type: TimeFilterType.LAST_24_HOURS,
    duration: 1440
};
const DEFAULT_REFRESH_INTERVAL = '1m';

export abstract class MeasuresComponent {

    private selectionSubj: Subject<MeasureOption[]>;
    private timeFilterSubj: Subject<TimeFilter>; // currently selected time filter
    private timeResolutionChanged: Subject<void>; // time resolution change trigger
    private measureRefreshSubj: BehaviorSubject<void>;
    protected gseDataUpdated: Subject<number | null> = new Subject<number | null>();

    protected destroyed = new Subject<void>();
    protected timeFilter: TimeFilter;
    protected timeZone: string = null; // configured time zone, used to align time ranges to local-day boundaries

    contentResize: Observable<void>;
    resource: MeasureResource | null; // currently selected resource
    timeResolutions: TimeResolution[];
    timeResolution: TimeResolution; // currently selected time resolution
    refreshIntervalResetter: () => void;
    options: MeasureOption[];
    selection: MeasureOption[];
    measures: Measure[];
    measuresType: MeasureDataType;

    // exported in HTML
    TimeFilterProfile = TimeFilterProfile;
    DEFAULT_TIME_FILTER = DEFAULT_TIME_FILTER;
    DEFAULT_REFRESH_INTERVAL = DEFAULT_REFRESH_INTERVAL;

    protected constructor(@Inject(APP_CONFIG_TOKEN) protected readonly cfg: AppConfig,
                          protected readonly resizeService: ResizeService,
                          protected readonly hierarchyService: HierarchyService,
                          protected readonly networkService: NetworkService,
                          protected readonly apiService: SmagApiService,
                          protected readonly configurationService: ConfigurationService) {
        this.resource = null;
        this.timeResolutions = getAvailableResolutionsForDuration(DEFAULT_TIME_FILTER.duration);
        this.timeFilterSubj = new ReplaySubject<TimeFilter>(1);
        this.timeResolution = getDefaultResolutionForDuration(DEFAULT_TIME_FILTER.duration);
        this.timeResolutionChanged = new Subject<void>();
        this.measureRefreshSubj = new BehaviorSubject<void>(undefined);
        this.refreshIntervalResetter = () => {
        };
        this.options = [];
        this.measures = [];
        this.selection = [];
        this.selectionSubj = new Subject<MeasureOption[]>();
        this.measuresType = null;
        this.timeFilter = null;
    }

    get gridColor(): string {
        return this.cfg.shared.gridColor;
    }

    get tooltipBorderColor(): string {
        return this.cfg.shared.tooltipBorderColor;
    }

    get tooltipBackgroundColor(): string {
        return this.cfg.shared.tooltipBackgroundColor;
    }

    get selectionLineColor(): string {
        return this.cfg.shared.selectionLineColor;
    }

    isOptionSelected(option: MeasureOption): boolean {
        return this.selection.includes(option); // reference check!
    }

    onSelectResource(selection: ECSelection) {
        if (selection.resourceId !== this.resource?.id) {
            this.hierarchyService.select(selection.resourceId);
        }
    }

    onSelectTimeFilter(event: TimeFilter) {
        this.timeFilter = event;
        this.timeFilterSubj.next(event);
    }

    onSelectTimeResolution(event: MatSelectChange) {
        this.timeResolution = event.value as TimeResolution;
        this.timeResolutionChanged.next(); // triggers reloading
    }

    onSelectOption(option: MeasureOption, selected: boolean) {
        if (selected) {
            this.selection = this.options.filter(opt => this.selection.includes(opt) || opt === option); // reference check!
        } else {
            this.selection = this.selection.filter(opt => opt !== option); // reference check!
        }
        this.selectionSubj.next(this.selection); // triggers reloading
    }

    onMeasureDrop(event: CdkDragDrop<Measure[], any>) {
        moveItemInArray(this.measures, event.previousIndex, event.currentIndex);
    }

    onRefreshMeasures() {
        this.refreshIntervalResetter();
        this.measureRefreshSubj.next();
    }

    onSelectTimeRangeFromChart(event: [number, number]) {
        const [fromTime, toTime] = event;
        if (toTime - fromTime > 3600000) {
            this.timeFilterComponent.setCustomTimeFilter(fromTime, toTime); // triggers emission on `timeFilter`
        }
    }

    protected abstract get timeFilterComponent(): TimeFilterComponent;

    protected onInit() {
        this.configurationService.timeConfig.pipe(
            takeUntil(this.destroyed)
        ).subscribe(timeCfg => this.timeZone = timeCfg.time_zone);
        this.contentResize = this.resizeService.observe(['content']).pipe(
            takeUntil(this.destroyed)
        );

        // NOTE: update of `this.resource` and `this.options` is demanded to child component
        //       in order to allow for custom (and potentially sync/async/mixed) routines

        combineLatest([
            this.selectionSubj,
            this.timeFilterSubj,
            this.timeResolutionChanged, // when this fires, `this.timeResolution` is assumed to have already been updated
            this.gseDataUpdated
        ]).pipe(
            takeUntil(this.destroyed)
        ).subscribe(([selection, timeFilter]) => {
            const dataTypes = selection.map(opt => opt.dataType);
            // selection may require further adjustment of time resolution
            this.adjustTimeResolution(dataTypes, timeFilter);
            const roundedFilter = roundToMinute(this.timeResolution.resolution, timeFilter, this.timeZone);
            this.updateMeasures(selection, roundedFilter, this.timeResolution);
            this.updateMeasuresType(this.measures);
        });
        this.timeResolutionChanged.next(); // initial trigger
        this.gseDataUpdated.next(); // initial trigger
    }

    protected onDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    protected updateSelectedResource(resource: MeasureResource) {
        this.resource = resource;
    }

    protected updateMeasureOptions(options: MeasureOption[]) {
        this.options = options;
        this.selection = this.options; // all options initially selected
        this.selectionSubj.next(this.selection);
    }

    protected adjustTimeResolutionForMeasureDataTypes(dataTypes: MeasureDataType[]) {
        if (!this.resource) {
            return;
        }
        // get the finest resolution allowed by (currently selected) measures
        let finestRes: TimeResolution = null;
        if (!!getStatisticalProfileDeviceKeys(this.resource).length) {
            // if the selected entity *can* have statistical profiles, the finest resolution depends on whether
            // profiles are *actually* configured for the entity; on the other hand, entities which cannot have
            // statistical profiles have no particular limitations
            if (dataTypes.find(dt => dt === MeasureDataType.STATISTICAL)) {
                // if we have *at least* one statistical profile set, we limit resolution to 1 hour
                finestRes = TIME_RESOLUTIONS.find(tr => tr.resolution === 60);
            } else {
                // for entities with no statistical profiles set, we limit resolution to 15'
                finestRes = TIME_RESOLUTIONS.find(tr => tr.resolution === 15);
            }
            // TODO: reason on connected meter type whenever available
        }
        if (finestRes) {
            const timeResolutions = this.timeResolutions.filter(tr => tr.resolution >= finestRes.resolution);
            if (!!timeResolutions.length) {
                this.timeResolutions = timeResolutions;
                // if currently selected resolution is "incompatible" with the finest one allowed,
                // select the finest available resolution
                if (!this.timeResolutions.find(tr => tr.resolution === this.timeResolution.resolution)) {
                    this.timeResolution = this.timeResolutions.reduce((p, c) => c.resolution < p.resolution ? c : p);
                }
            }
        }
    }

    protected updateMeasuresType(measures: Measure[]) {
        // let's examine single measures...
        this.measuresType = null;
        for (const measure of measures) {
            if (isDefined(measure.dataType) && measure.dataType !== this.measuresType) {
                this.measuresType = this.measuresType === null ? measure.dataType : MeasureDataType.HYBRID;
                if (this.measuresType === MeasureDataType.HYBRID) {
                    return; // no need for further reasoning
                }
            }
        }
    }

    protected mkMeasureDataType(deviceKey: string): MeasureDataType {
        if (hasFieldEntityDevice(this.resource, deviceKey)) {
            return MeasureDataType.MONITORED;
        } else if (hasStatisticalProfile(this.resource, deviceKey)) {
            return MeasureDataType.STATISTICAL;
        } else {
            return null;
        }
    }

    protected loadDeviceData(deviceKey: string,
                             timeAggregation: TimeAggregationType,
                             dataSource: MeasureAggregationDataSource,
                             coefficient: number,
                             timeRange: [number, number],
                             timeRes: number,
                             resourceId?: string): Observable<MeasureData> {
        const coeff = isDefined(coefficient) ? coefficient : 1;
        const [startTime, endTime] = timeRange;
        const internalResourceId = resourceId ? resourceId : this.resource.id;

        return this.apiService.aggregateMeasures(internalResourceId,
            [deviceKey],
            startTime,
            `${Math.round((endTime - startTime) / 60000)} ${TIME_UNITS.MINUTES}`,
            `${timeRes} ${TIME_UNITS.MINUTES}`,
            timeAggregation,
            dataSource,
            false).pipe(
            map(resp => ({
                data: resp.times.map((t, i) => [t, fixValue(resp.values[deviceKey][i], coeff)]),
                fallbackTimes: new Set<number>((resp.fallback_times || {})[deviceKey] || [])
            }))
        );
    }

    protected loadCollapsedDeviceData(deviceKey: string,
                                      timeAggregation: TimeAggregationType,
                                      dataSource: MeasureAggregationDataSource,
                                      timeRange: [number, number]): Observable<number> {
        const [startTime, endTime] = timeRange;

        return this.apiService.aggregateMeasures(this.resource.id,
            [deviceKey],
            startTime,
            `${Math.round((endTime - startTime) / 60000)} ${TIME_UNITS.MINUTES}`,
            `60 ${TIME_UNITS.MINUTES}`, // always 1 hour minutes for ECs
            timeAggregation,
            dataSource,
            true).pipe(
            map(resp => resp.values[deviceKey]?.length ? resp.values[deviceKey][0] : null)
        );
    }

    protected loadDeviceUom(deviceKey: string, defaultUom?: string): Observable<string> {
        if (hasFieldEntityDevice(this.resource, deviceKey)) {
            return this.apiService.getConnectorDevice((this.resource.connector_devices || {})[deviceKey])
                .pipe(
                    map(d => d.uom || defaultUom || null),
                    catchError(e => {
                        // fall back on supplied default measure if the device is not found
                        if (errorCode(e) === ErrorCode.DEVICE_NOT_FOUND) {
                            return of(defaultUom || null);
                        }
                        return throwError(e);
                    })
                );
        }
        return of(defaultUom || null);
    }

    protected loadAggregationData(measureAggregationType: EntityMeasureAggregationType,
                                  timeAggregation: TimeAggregationType,
                                  dataSource: MeasureAggregationDataSource,
                                  coefficient: number,
                                  timeRange: [number, number],
                                  timeRes: number): Observable<MeasureData> {
        const coeff = isDefined(coefficient) ? coefficient : 1;
        const [startTime, endTime] = timeRange;

        return this.apiService.aggregateEntityMeasures(this.resource.id,
            measureAggregationType,
            startTime,
            `${Math.round((endTime - startTime) / 60000)} ${TIME_UNITS.MINUTES}`,
            `${timeRes} ${TIME_UNITS.MINUTES}`,
            timeAggregation,
            dataSource,
            false,
            false).pipe(
            map(resp => ({
                data: resp.times.map((t, index) => [t, fixValue(resp.values[index], coeff)]),
                fallbackTimes: new Set<number>(resp.fallback_times || [])
            }))
        );
    }


    protected loadCollapsedAggregationData(aggregationType: EntityMeasureAggregationType,
                                           timeAggregation: TimeAggregationType,
                                           dataSource: MeasureAggregationDataSource,
                                           timeRange: [number, number], resourceId?: string): Observable<number> {
        const [startTime, endTime] = timeRange;
        const internalResourceId = resourceId ? resourceId : this.resource.id;
        return this.apiService.aggregateEntityMeasures(internalResourceId,
            aggregationType,
            startTime,
            `${Math.round((endTime - startTime) / 60000)} ${TIME_UNITS.MINUTES}`,
            `60 ${TIME_UNITS.MINUTES}`, // always 1 hour minutes for ECs
            timeAggregation,
            dataSource,
            true,
            true).pipe(
            map(agg => agg.values.length ? agg.values[0] : null)
        );
    }

    private updateMeasures(sel: MeasureOption[], timeFilter: TimeFilter, timeRes: TimeResolution) {
        const rangeRefreshObs: Observable<[number, number]> = this.measureRefreshSubj.pipe(
            map(() => {
                const range = timeFilter.range();
                return [range.fromTime, range.toTime];
            })
        );
        // try to load new measures' data while at the same time keeping current measure order
        // (possibly changed via drag&drop)
        // TODO: persist measures' order in local storage or user configuration
        // this.measures = this.measures
        //     .map(m => newMeasures.find(nm => nm.name === m.name)) // overwrite with same measure in new set
        //     .filter(m => !!m) // keep only measures that exists in new set
        //     .concat(newMeasures
        //         .filter(nm => !this.measures.find(m => m.name === nm.name))); // add new measures that did not exist before
        this.measures = sel.map(opt => mkMeasure(opt, rangeRefreshObs, timeRes));
    }

    private adjustTimeResolution(dataTypes: MeasureDataType[], timeFilter: TimeFilter) {
        // adjust time resolution based on time filter
        this.adjustTimeResolutionForTimeFilter(timeFilter);
        // adjust time resolution based on entity type and potentially configured statistical profiles
        this.adjustTimeResolutionForMeasureDataTypes(dataTypes);
    }

    private adjustTimeResolutionForTimeFilter(timeFilter: TimeFilter) {
        this.timeResolutions = getAvailableResolutionsForDuration(timeFilter.duration);
        // if currently selected resolution is "incompatible" with the selected time filter, fall back to default resolution
        if (!this.timeResolutions.find(tr => tr.resolution === this.timeResolution.resolution)) {
            this.timeResolution = getDefaultResolutionForDuration(timeFilter.duration);
        }
    }
}

function mkMeasure(option: MeasureOption, rangeRefreshObs: Observable<[number, number]>, timeRes: TimeResolution): Measure {
    return {
        name: option.name,
        dataType: option.dataType,
        data: option.dataFactory(rangeRefreshObs, timeRes.resolution)
    };
}

function getAvailableResolutionsForDuration(duration: number): TimeResolution[] {
    // keep only resolutions that are not larger that interval duration
    const availableResolutions = TIME_RESOLUTIONS.filter(tr => tr.resolution <= Math.max(duration, 1));
    if (duration > 10080) {
        // only resolutions >= 1h available for durations > 1 week
        return availableResolutions.filter(tr => tr.resolution >= 60);
    }
    return availableResolutions;
}

function getDefaultResolutionForDuration(duration: number): TimeResolution {
    // prefer 1h whenever available, otherwise scale down to the largest possible resolution
    if (duration >= 1440) { // includes durations > 1 week
        return TIME_RESOLUTIONS.find(tr => tr.resolution === 60);
    } else if (duration >= 60) {
        return TIME_RESOLUTIONS.find(tr => tr.resolution === 60);
    } else if (duration >= 15) {
        return TIME_RESOLUTIONS.find(tr => tr.resolution === 15);
    } else if (duration >= 5) {
        return TIME_RESOLUTIONS.find(tr => tr.resolution === 5);
    } else {
        return TIME_RESOLUTIONS.find(tr => tr.resolution === 1);
    }
}

function fixValue(value: number, coeff: number): number {
    return isDefined(value) ? coeff * value : value;
}

function roundToMinute(durationMins: number, timeFilter: TimeFilter, timeZone: string): TimeFilter {

    return {
        type: timeFilter.type,
        duration: timeFilter.duration,
        range: () => {
            const timeRange = timeFilter.range();
            const roundedFrom = floorToResolution(timeRange.fromTime, durationMins, timeZone);
            const roundedTo = floorToResolution(timeRange.toTime, durationMins, timeZone);

            return  {
                fromTime: roundedFrom,
                toTime: roundedTo <= roundedFrom ? roundedFrom : roundedTo,
                includesNow: false
            } as TimeRange;
        }
    } as TimeFilter;
}

// Floors an epoch-ms timestamp to a multiple of `durationMins`, measured from the start of the day in the
// given time zone (NOT from the UNIX epoch / UTC midnight). This keeps day-aligned resolutions (e.g. the
// "1 day" unit = 1440 min) snapped to the user's local midnight rather than to UTC midnight, which otherwise
// shifts the range by the time-zone offset and can land it on the wrong calendar day.
function floorToResolution(time: number, durationMins: number, timeZone: string): number {
    const zone = timeZone || DEFAULT_TIMEZONE;
    const dayStart = moment.tz(time, zone).startOf('day');
    // Whole-day resolutions snap straight to local midnight via calendar arithmetic, which is DST-safe.
    // (On a fall-back day the day is 25h long, so ms-based flooring would mis-bucket its last hour to 23:00.)
    if (durationMins % 1440 === 0) {
        return dayStart.valueOf();
    }
    // Sub-day resolutions all divide an hour evenly; since DST shifts are whole hours, flooring by elapsed
    // ms from local midnight lands exactly on wall-clock boundaries.
    const deltaMs = durationMins * 60000;
    const startOfDay = dayStart.valueOf();
    const msSinceMidnight = time - startOfDay;
    return startOfDay + (msSinceMidnight - (msSinceMidnight % deltaMs));
}
